import { AxiosError, AxiosResponse } from 'axios';
import isWebView from 'is-ua-webview';
import { action, observable, runInAction } from 'mobx';
import qs from 'qs';
import { UiResponse } from '../CommonInterfaces';
import { ACCOUNT, ACCOUNT_MERGE_PATH, LARES_POST_FORM_PATH, MANDATES_PAGE_PATH, UI_WEB_API_PATH, View } from '../Routes';
import { API, FetchStatus } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import GaDataStore from './GaDataStore';
import RememberMeStore from './RememberMeStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import UiStore from './UiStore';

export const mapAuthServiceToGaAuthType: { [key: string]: string } = {
  sebService: 'SEB',
  swedService: 'Swedbank',
  nordeaIPService: 'Luminor',
  kredService: 'Coop Pank',
  lhvService: 'LHV'
};

export default class LoginReturnStore {
  @observable accessor uiResponse: UiResponse = {};
  @observable accessor status: string = '';

  private routeStore: RouteStore;
  private uiStore: UiStore;
  private rememberMeStore: RememberMeStore;

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.uiStore = rootStore.uiStore;
    this.rememberMeStore = rootStore.rememberMeStore;
  }

  public loadLoginReturnData = async () => {
    runInAction(() => {
      this.status = FetchStatus.FETCHING;
    });
    try {
      const { data } = await API.get<UiResponse>('/return/uiResponse', { params: this.routeStore.decodedParams });
      runInAction(() => {
        this.uiResponse = data;
        this.handleSuccess(data);
        this.status = FetchStatus.FETCHED;
      });
    } catch (e: Error | string | any) {
      this.handleError(e);
      runInAction(() => {
        this.status = FetchStatus.ERROR;
      });
    }
  };

  private preserveWebapiParamForAppLogin = (data: UiResponse): { webapi?: boolean } => {
    const params = this.routeStore.decodedParams || {};
    if (params.webapi) {
      return {};
    }
    const parsedUrlParams = qs.parse(data.params?.urlParams);
    return { webapi: !!parsedUrlParams?.webapi && (!!parsedUrlParams?.deeplink || isWebView(navigator.userAgent)) };
  };

  @action.bound
  public handleSuccess(data: UiResponse) {
    GaDataStore.loginStatus(true, mapAuthServiceToGaAuthType[data.params?.service], this.rememberMeStore.rememberMe);
    const params = this.routeStore.decodedParams || {};
    if (data.redirectUrl) {
      this.routeStore.route(data.redirectUrl);
    } else if (data.component) {
      switch (data.component) {
        case View.MANDATES:
          this.routeStore.route(MANDATES_PAGE_PATH + this.getQueryString({ ...params, ...this.preserveWebapiParamForAppLogin(data) }));
          break;
        case View.LARES:
          this.routeStore.route(LARES_POST_FORM_PATH + this.getQueryString(data.params));
          break;
        case View.ACCOUNT_MERGE:
          this.routeStore.route(ACCOUNT_MERGE_PATH + this.getQueryString({ ...params, ...data.params }));
          break;
        case View.ACCOUNT:
          this.routeStore.route(ACCOUNT + this.routeStore.preserveParams(['locale', 'app']));
          break;
        case View.WEB_API:
          this.routeStore.route(
            `${UI_WEB_API_PATH}${qs.stringify(
              {
                allowStatisticsCookies: params.allowStatisticsCookies,
                goto: params.goto,
                deeplink: params.deeplink
              },
              { addQueryPrefix: true }
            )}`
          );
          break;
        default:
          window.TeliaRum?.sendError(new Error(`Unhandled external login return component: ${data.component}`));
      }
    } else {
      this.routeStore.route('/');
    }
    this.uiStore.handleNotice({ data } as AxiosResponse);
    this.uiResponse = data;
  }

  public handleError = (e: AxiosError<UiResponse>) => {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    if (e.response?.data.redirectUrl) {
      return this.routeStore.route(e.response.data.redirectUrl);
    }
    this.uiStore.showErrorNoticeFromResponse(e.response);
  };

  private getQueryString(params?: { [p: string]: any }) {
    return params ? qs.stringify(params, { addQueryPrefix: true }) : '';
  }
}
